import { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { get } from "lodash";
import { useMutation, useQuery } from "@apollo/client";
import classNames from "classnames";
import { FiCheck } from "react-icons/fi";
import { NavLink, useSearchParams } from "react-router-dom";
import { areTermsAndConditionsUpToDate } from "../../../utils/tos";
import { motion } from "framer-motion";
import { RxCaretLeft } from "react-icons/rx";
import { useAuth } from "../../../context/AuthContext";
import { UPDATE_MANDATORY_INFO } from "../query";
import { SEARCH_QUERY } from "../../../queries/search";

type InputMandatoryFields = {
  country_id: string;
  organization_raw: string;
  organization_raw_type: string;
  role: string;
  terms_conditions_accepted: boolean;
  system_user_type: string;
};

export const StepSelectType = ({ types, onSuccess, step = 0 }: any) => {
  const { session } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    reset,
    trigger,
    formState: { errors },
  } = useForm<InputMandatoryFields>({
    defaultValues: {
      system_user_type: get(session, "user.system_user_type", undefined),
      country_id: get(session, "user.country.id", undefined),
      organization_raw: get(session, "user.organization_raw", undefined),
      organization_raw_type: get(
        session,
        "user.organization_raw_type",
        undefined,
      ),
      role: get(session, "user.role", undefined),
      // Force unchecked if ToS is outdated so the user must actively re-accept
      terms_conditions_accepted: areTermsAndConditionsUpToDate(
        get(session, "user.terms_conditions_accepted_at", undefined),
      )
        ? (get(session, "user.terms_conditions_accepted", false) ?? false)
        : false,
    },
  });

  useEffect(() => {
    // Only reset form when session user data is loaded
    if (session?.user) {
      const tosUpToDate = areTermsAndConditionsUpToDate(
        get(session, "user.terms_conditions_accepted_at", undefined),
      );
      reset({
        system_user_type: get(session, "user.system_user_type", undefined),
        country_id: get(session, "user.country.id", undefined),
        organization_raw: get(session, "user.organization_raw", undefined),
        organization_raw_type: get(
          session,
          "user.organization_raw_type",
          undefined,
        ),
        role: get(session, "user.role", undefined),
        // Force unchecked if ToS is outdated so the user must actively re-accept
        terms_conditions_accepted: tosUpToDate
          ? (get(session, "user.terms_conditions_accepted", false) ?? false)
          : false,
      });

      trigger(); // Optionally validate after resetting
    }
  }, [session?.user?.id, reset, trigger]); // Use user.id to avoid reacting to every session change

  const [updateInfo, { error }] = useMutation(UPDATE_MANDATORY_INFO, {
    onCompleted: () => onSuccess(getValues()),
    onError: () => {},
  });

  const { data, loading } = useQuery(SEARCH_QUERY, {
    onCompleted: () => {
      setValue("country_id", get(session, "user.country.id", ""));
    },
    variables: {
      search: {
        bucket: "countries",
        query: {},
        sort: [
          {
            key: "name.keyword",
            dir: "asc",
          },
        ],
        size: 500,
      },
    },
  });

  const countries = get(data, "search.items", []).map((s: any) => ({
    id: s.id,
    name: s.name,
  }));

  const onSubmit: SubmitHandler<InputMandatoryFields> = (data) => {
    updateInfo({
      variables: {
        user_id: session?.user?.id,
        req: { ...data },
      },
    });
  };

  if (loading) {
    return <p className="py-4">Loading options...</p>;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full ">
      {error && (
        <p className="text-sm bg-red-100 text-red-900 px-2 py-1">
          Theres an error with the server. Please try again or contact
          support@briterbridges.com
        </p>
      )}
      <input
        type="text"
        className="hidden"
        hidden
        {...register("system_user_type", { required: true })}
      />
      <motion.div
        className={classNames({
          "flex w-full flex-col gap-6 py-8 select-none max-w-4xl mx-auto": true,
          hidden: types.some(
            (item: any) => item.type === getValues("system_user_type"),
          ),
        })}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
      >
        <div className="text-center space-y-3">
          <p className="uppercase text-gray-500 font-semibold text-xs tracking-wide">
            Getting Started
          </p>
          <h2 className="text-3xl font-bold text-gray-900">
            How will you use the platform?
          </h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Choose the option that best describes your role. This helps us
            personalize your experience and show you the most relevant insights
            and features.
          </p>
        </div>

        {(() => {
          const gridTypes = types.filter((u: any) => !u.standalone);
          const standaloneTypes = types.filter((u: any) => u.standalone);
          return (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-8">
                {gridTypes.map((u: any) => {
                  const isSelected = getValues("system_user_type") === u.type;
                  return (
                    <button
                      key={u.label}
                      type="button"
                      className={classNames({
                        "relative w-full text-left px-6 py-5 rounded-lg border-2 transition-all duration-200 md:last:col-span-2": true,
                        "border-uw-primary bg-uw-primary/5 shadow-sm": isSelected,
                        "border-gray-200 hover:border-gray-300 hover:bg-gray-50": !isSelected,
                      })}
                      onClick={(ev) => {
                        ev.preventDefault();
                        setValue("system_user_type", u.type, {
                          shouldValidate: true,
                          shouldDirty: true,
                          shouldTouch: true,
                        });
                      }}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h3
                            className={classNames({
                              "font-semibold text-lg mb-1": true,
                              "text-uw-primary": isSelected,
                              "text-gray-900": !isSelected,
                            })}
                          >
                            {u.label}
                          </h3>
                          <p className="text-sm text-gray-600 leading-relaxed">
                            {u.description}
                          </p>
                        </div>
                        {isSelected && (
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-uw-primary flex items-center justify-center">
                            <FiCheck className="text-white text-sm" />
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              {standaloneTypes.length > 0 && (
                <div className="mt-6 text-center">
                  <p className="text-xs text-gray-400 mb-3">— or —</p>
                  {standaloneTypes.map((u: any) => {
                    const isSelected = getValues("system_user_type") === u.type;
                    return (
                      <button
                        key={u.type}
                        type="button"
                        onClick={(ev) => {
                          ev.preventDefault();
                          setValue("system_user_type", u.type, {
                            shouldValidate: true,
                            shouldDirty: true,
                            shouldTouch: true,
                          });
                        }}
                        className={classNames({
                          "text-sm px-4 py-2 rounded-lg border transition-all duration-200": true,
                          "border-uw-primary bg-uw-primary/5 text-uw-primary font-medium": isSelected,
                          "border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700": !isSelected,
                        })}
                      >
                        {isSelected && <FiCheck className="inline mr-1.5 text-xs" />}
                        {u.description}
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          );
        })()}
      </motion.div>
      {getValues("system_user_type") && (
        <motion.div
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 100 }}
          transition={{ duration: 0.4 }}
          className="w-full gap-4 py-8 flex flex-col"
        >
          <p className="text-2xl font-semibold flex items-center gap-4">
            <button
              type="button"
              onClick={(ev) => {
                ev.preventDefault();
                //@ts-ignore
                setValue("system_user_type", undefined, {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                });
              }}
              className="bg-gray-200 text-center rounded-full p-0.5"
            >
              <RxCaretLeft className="h-5 w-5" />
            </button>
            <span>Which organisation do you represent?</span>
          </p>
          <div className="flex flex-col gap-2">
            <label className="font-medium">Role, position or title</label>
            <select
              {...register("role", { required: true })}
              id="role"
              aria-invalid={errors.role ? "true" : "false"}
              className={classNames({
                "w-full border px-4 py-1.5 rounded-md ": true,
                "outline-red-500": errors.role,
                "outline-input-focus": !errors.role,
              })}
            >
              <option value="" disabled hidden>
                Select an option
              </option>
              {[
                "CEO or Partner",
                "Non-CEO C-suite",
                "Investment Manager",
                "Journalist",
                "Student or Researcher",
                "Associate or Analyst",
                "Other",
              ].map((role, index) => (
                <option key={index} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-2">
            <label className="font-medium">Company or organisation</label>
            <input
              id="organization_raw"
              aria-invalid={errors.organization_raw ? "true" : "false"}
              {...register("organization_raw", {
                required: true,
                minLength: 2,
              })}
              className={classNames({
                "w-full border px-4 py-1.5 rounded-md ": true,
                "outline-red-500": errors.organization_raw,
                "outline-input-focus": !errors.organization_raw,
              })}
              placeholder="Which company do you represent or work for?"
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="font-medium">Organisation Type</label>
            <select
              {...register("organization_raw_type", { required: true })}
              id="organization_raw_type"
              aria-invalid={errors.organization_raw_type ? "true" : "false"}
              className={classNames({
                "w-full border px-4 py-1.5 rounded-md ": true,
                "outline-red-500": errors.organization_raw_type,
                "outline-input-focus": !errors.organization_raw_type,
              })}
            >
              <option value="" disabled hidden>
                Select an option
              </option>
              {[
                "Accelerator, Incubator, Venture Builder, or other Entrepreneur Support Organisation",
                "AgTech or Startup",
                "Agribusiness, Agri-input Supplier or Agro Dealer",
                "Consultant or Individual",
                "Development Finance Institution (DFI) or Multilateral Development Bank (MDB)",
                "Producer Association or Cooperative",
                "Government or Public Sector Agency",
                "Investor",
                "Non-Governmental Organisation, Non-Profit, or Foundation",
                "Research Organisation or University",
                "Other private sector company",
              ].map((type, index) => (
                <option key={index} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-2">
            <label className="font-medium">Country (your primary place)</label>
            <select
              id="country_id"
              aria-invalid={errors.country_id ? "true" : "false"}
              {...register("country_id", { required: true })}
              className="w-full border px-4 py-1.5 rounded-md outline-input-focus"
            >
              <option value="" disabled hidden>
                {loading ? "Loading..." : "Select an option"}
              </option>
              {countries.map((c: any, index: number) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div
            className={classNames({
              "w-full flex flex-col gap-2": true,
              hidden:
                get(session, "user.terms_conditions_accepted", false) &&
                areTermsAndConditionsUpToDate(
                  get(session, "user.terms_conditions_accepted_at", undefined),
                ),
            })}
          >
            <h2 className="text-2xl font-semibold py-2">
              We recently changed our Terms and Conditions.
            </h2>
            <div className="flex gap-2 items-center">
              <input
                type="checkbox"
                id="terms_conditions_accepted"
                aria-invalid={
                  errors.terms_conditions_accepted ? "true" : "false"
                }
                {...register("terms_conditions_accepted", { required: true })}
              />{" "}
              <p>
                I agree to the{" "}
                <NavLink
                  to="/terms-of-use"
                  target="_blank"
                  className="underline"
                >
                  Terms and Conditions
                </NavLink>
              </p>
            </div>
            <p className="text-xs py-1">
              We recently updated our Terms and Conditions, and you must accept
              them to continue using Briter Intelligence. If you have any
              questions or issues, please contact support@briterbridges.com.
            </p>
          </div>
          {errors.terms_conditions_accepted && (
            <p className="bg-red-100 rounded-lg px-4 py-2 text-red-900 text-xs">
              This checkbox is mandatory
            </p>
          )}
          <div>
            <button
              type="submit"
              className="px-8 py-2 bg-uw-primary hover:text-uw-text-primary text-uw-text-primary rounded disabled:bg-gray-300"
            >
              Save & continue
            </button>
          </div>
        </motion.div>
      )}
    </form>
  );
};
